import { Button, ConfigProvider, Form, Input } from "antd";
import { LockOutlined, UserOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import useAxiosClient from "../axios-client.js";
import { useStateContext } from "../contexts/ContextProvider.jsx";
import elegantTexLogo from '../assets/images/eleganttex-logo-only.png'
import { usePublicCompany } from "../hooks/useCompany.jsx";
import { logoSrc } from "../utils/company";

// Design tokens ported from the "Login Redesign" mock, same shell palette as
// DefaultLayout and the redesigned order/access screens. Login sits outside
// DefaultLayout (no shell chrome), so the CSS variables are set on this
// component's own root instead of being inherited from the shell.
import { PRIMARY, SHELL_THEME as THEME, shellAntdTheme, STATUS } from "../shellTokens";

export default function Login() {

  const axiosClient = useAxiosClient();
  const { setToken, message, setMessage, darkMode } = useStateContext();
  // Branding only — /company/public answers without a token and returns the
  // name, tagline and logo, nothing a signed-out visitor should not see.
  const company = usePublicCompany();
  const brandLogo = logoSrc(company) || elegantTexLogo;

  const [loading, setLoading] = useState(false);
  // Two independent strips instead of one message replacing the heading:
  // `notice` is a neutral, auto-clearing value from context (e.g. after a
  // password reset); `loginError` is a rejected sign-in and persists until
  // the next submit.
  const [notice, setNotice] = useState(null);
  const [loginError, setLoginError] = useState(null);

  useEffect(() => {
    if (!message) return;
    setNotice(message);
    setMessage("");
    const timer = setTimeout(() => setNotice(null), 15000);
    return () => clearTimeout(timer);
  }, [message, setMessage]);

  const onFinish = (values) => {
    setLoginError(null);
    setLoading(true);
    axiosClient.post('auth/login', { email: values.email, password: values.password })
      .then(({ data }) => {
        setToken(data.access_token, data.expires_in);
      })
      .catch((error) => {
        const errorMessage = (error.response && error.response.data && error.response.data.message)
          || error.message || error.toString();
        setLoginError(errorMessage);
        setLoading(false);
      });
  };

  const rootStyle = {
    ...Object.fromEntries(
      Object.entries(THEME[darkMode ? "dark" : "light"]).map(([k, v]) => [`--${k}`, v])
    ),
    "--primary": PRIMARY,
  };

  return (
    <div className="et-login" style={rootStyle}>
      <style>{`
        .et-login{
          min-height:100vh;display:flex;align-items:center;justify-content:center;
          background:var(--page);font-family:Outfit,system-ui,sans-serif;padding:24px 16px;
        }
        .et-login__wrap{width:100%;max-width:420px;display:flex;flex-direction:column;gap:18px}
        .et-login__card{
          background:var(--surface);border:1px solid var(--border);border-radius:14px;
          padding:32px 30px 30px;display:flex;flex-direction:column;gap:22px;
        }
        .et-login .ant-form-item{margin-bottom:0}
        .et-login .ant-form-item-label{padding-bottom:6px}
        @media (max-width:480px){
          .et-login{padding:0}
          .et-login__wrap{max-width:none;min-height:100vh;justify-content:space-between}
          .et-login__card{
            flex:1;justify-content:center;border:none;border-radius:0;background:var(--page);
          }
          .et-login .ant-input,.et-login .ant-input-affix-wrapper{min-height:44px}
          .et-login .ant-btn-lg{min-height:48px}
        }
      `}</style>

      <div className="et-login__wrap">
        <div className="et-login__card">
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
            <div style={{
              width: 96, height: 64, borderRadius: 12, background: "rgba(255,255,255,.94)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <img src={brandLogo} alt={company.name} style={{ height: 56, width: "auto", display: "block" }} />
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 21, fontWeight: 700, letterSpacing: "-.01em", color: "var(--text)" }}>
                Welcome back
              </div>
              <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 3 }}>
                Sign in to {company.name}
              </div>
            </div>
          </div>

          {notice && !loginError && (
            <div style={{
              border: "1px solid var(--border)", background: "var(--surface2)", borderRadius: 9,
              padding: "10px 12px", display: "flex", gap: 9, alignItems: "flex-start",
            }}>
              <div style={{ flexShrink: 0, width: 6, height: 6, borderRadius: 3, background: "var(--muted)", marginTop: 5 }} />
              <div style={{ fontSize: 12.5, lineHeight: 1.45, color: "var(--muted)" }}>{notice}</div>
            </div>
          )}

          <ConfigProvider theme={shellAntdTheme(darkMode)}>
            <Form
              name="login"
              layout="vertical"
              disabled={loading}
              onFinish={onFinish}
              autoComplete="off"
              style={{ display: "flex", flexDirection: "column", gap: 14 }}
            >
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please input your email!' },
                  { type: 'email', message: 'Please enter a valid email!' },
                ]}
              >
                <Input
                  prefix={<UserOutlined style={{ color: "var(--dim)" }} />}
                  placeholder="Enter your email"
                />
              </Form.Item>

              <Form.Item
                name="password"
                label="Password"
                rules={[{ required: true, message: 'Please input your password!' }]}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: "var(--dim)" }} />}
                  placeholder="Enter your password"
                />
              </Form.Item>

              {loginError && (
                <div style={{
                  border: "1px solid rgba(248,113,113,.35)", background: "rgba(248,113,113,.1)", borderRadius: 9,
                  padding: "10px 12px", display: "flex", gap: 9, alignItems: "flex-start",
                }}>
                  <div style={{ flexShrink: 0, width: 6, height: 6, borderRadius: 3, background: STATUS.error, marginTop: 5 }} />
                  <div style={{ fontSize: 12.5, lineHeight: 1.45, fontWeight: 500, color: STATUS.errorSoft }}>
                    {loginError}
                  </div>
                </div>
              )}

              <Form.Item style={{ marginBottom: 0 }}>
                <Button type="primary" htmlType="submit" block size="large" loading={loading} style={{ fontWeight: 600 }}>
                  Sign in
                </Button>
              </Form.Item>
            </Form>
          </ConfigProvider>

          <div style={{ fontSize: 12, lineHeight: 1.5, color: "var(--dim)", textAlign: "center" }}>
            Locked out? Ask an admin to reset your password.
          </div>
        </div>

        <div style={{ fontSize: 11.5, color: "var(--dim)", textAlign: "center" }}>
          {company.name} © {new Date().getFullYear()}
        </div>
      </div>
    </div>
  )
}
